export type NotizStatus = 'TRANSKRIPTION' | 'AUSWERTUNG' | 'FERTIG' | 'FEHLER'

export interface PersonKurz {
  id: number
  name: string
}

export interface Notiz {
  id: number
  clientId: string
  aufgenommenAm: string
  zeitzone: string
  dauerMs: number | null
  hatAudio: boolean
  transkript: string | null
  zusammenfassung: string | null
  verwertbar: boolean
  status: NotizStatus
  fehler: string | null
  tags: string[]
  personen: PersonKurz[]
  termine: TerminKurz[]
  /** Freunde, mit denen die Notiz geteilt ist */
  geteiltMit: string[]
  /** Bei einer übernommenen Notiz eines Freundes: wer sie verfasst hat */
  uebernommenVon: string | null
  /** Antwort auf eine Rückfrage der Stichhaltigkeitsprüfung; wird selbst nicht geprüft */
  klarstellung: boolean
  /** Aus dem Tagebuch ausgeblendet */
  archiviert: boolean
  /** Antworten auf Fragen, die in der Notiz gestellt wurden */
  antworten: NotizAntwort[]
  /** Erinnerungen, um die in der Notiz gebeten wurde */
  erinnerungen: ErinnerungKurz[]
  /** Die Nachfrage der App, auf die diese Notiz antwortet */
  nachfrage?: string | null
}

export type ErinnerungStatus = 'OFFEN' | 'GESENDET' | 'ERLEDIGT'

export interface ErinnerungKurz {
  id: number
  text: string
  faelligAm: string
  zeitzone: string
  status: ErinnerungStatus
}

export interface Erinnerung extends ErinnerungKurz {
  notizId: number | null
}

export interface PushStand {
  /** Oeffentlicher VAPID-Schluessel; null, wenn der Server keine Benachrichtigungen schicken kann */
  schluessel: string | null
  geraete: number
}

export interface NotizAntwort {
  id: number
  frage: string
  antwort: Antwort
}

export interface Freigabe {
  benutzerId: number
  name: string
  geteiltAm: string
  uebernommen: boolean
}

/** Mit mir geteilte Notiz: nur Inhalt, nie die Kartei des Verfassers */
export interface GeteilteNotiz {
  id: number
  vonId: number
  von: string
  aufgenommenAm: string
  zeitzone: string
  dauerMs: number | null
  hatAudio: boolean
  transkript: string | null
  zusammenfassung: string | null
  breite: number | null
  laenge: number | null
  geteiltAm: string
  gesehen: boolean
  uebernommenAls: number | null
}

export type TerminStatus = 'VORGESCHLAGEN' | 'BESTAETIGT' | 'VERWORFEN'

export interface TerminKurz {
  id: number
  titel: string
  /** YYYY-MM-DD */
  datum: string
  /** HH:MM:SS, null = ganztaegig */
  uhrzeit: string | null
  status: TerminStatus
}

export interface Termin extends TerminKurz {
  notizId: number | null
  beginn: string
  ende: string | null
  ganztaegig: boolean
  ort: string
  zeitzone: string
  personen: PersonKurz[]
}

export interface TerminEingabe {
  titel: string
  datum: string
  uhrzeit: string | null
  dauerMinuten: number | null
  ort: string
}

export interface Quelle {
  nr: number
  art: 'ICH' | 'NOTIZ' | 'GETEILT' | 'PERSON' | 'TERMINE'
  id: number | null
  titel: string
  auszug: string
}

export interface Antwort {
  antwort: string
  quellen: Quelle[]
}

/** Verschlüsselung: aktiver Schlüssel und je anderem Schlüssel die Zahl noch umzustellender Werte */
export interface Schluesselstand {
  aktiv: number
  konfiguriert: number[]
  verbleibend: Record<string, number>
}

export interface BenutzerEintrag {
  id: number
  benutzername: string
  email: string | null
  emailVerifiziert: boolean
  rolle: 'ADMIN' | 'NUTZER'
  aktiv: boolean
  hatPasswort: boolean
  einladungBis: string | null
  erstelltAm: string
  anzahlNotizen: number
}

export interface FreundeAnsicht {
  freunde: { benutzerId: number; name: string; seit: string }[]
  eingehend: { benutzerId: number; name: string; am: string }[]
  /** "an" ist die eigene Eingabe (Name oder Adresse), nie der Name hinter einer Adresse */
  ausgehend: { id: number; an: string; am: string }[]
  gesperrt: { benutzerId: number; name: string }[]
}

export interface Profil {
  benutzername: string
  email: string | null
  emailVerifiziert: boolean
  ausstehendeEmail: string | null
}

export interface PersonUebersicht {
  id: number
  name: string
  beschreibung: string
  aliase: string[]
  anzahlFakten: number
  zuletzt: string | null
}

export interface Fakt {
  id: number
  fakt: string
  notizId: number | null
  aufgenommenAm: string | null
  zeitzone: string | null
}

/** Notiz, in der eine falsche Schreibweise vorkommt */
export interface Betroffen {
  id: number
  aufgenommenAm: string
  zeitzone: string
  auszug: string
}

export interface KorrekturAngebot {
  falsch: string
  richtig: string
  notizen: Betroffen[]
}

/** Gesprochene Korrektur („das heißt nicht X, sondern Y“), noch nicht bestätigt */
export interface KorrekturVorschlag {
  vorschlag: { id: number; notizId: number; aufgenommenAm: string; zeitzone: string; falsch: string; richtig: string }
  notizen: Betroffen[]
}

/** Rückfrage der Stichhaltigkeitsprüfung */
export interface PruefFrage {
  id: number
  notizId: number
  aufgenommenAm: string
  zeitzone: string
  daten: {
    art: 'SCHREIBWEISE' | 'WIDERSPRUCH'
    frage: string
    varianten: string[]
    aussagen: { text: string; beleg: { art: Quelle['art']; id: number | null; titel: string } | null }[]
    begruendung: string
  }
}

export interface Schreibweise {
  id: number
  falsch: string
  richtig: string
  abgelehnt: boolean
}

export interface BekanntePerson {
  id: number
  name: string
  aliase: string[]
  beschreibung: string
}

export interface ErkanntePerson {
  name: string
  beschreibung: string
  aliase: string[]
  fakten: string[]
  kandidaten: number[]
  begruendung: string
}

export interface KlaerungAnsicht {
  klaerung: { id: number; notizId: number; aufgenommenAm: string; zeitzone: string; transkript: string | null; person: ErkanntePerson }
  kandidaten: BekanntePerson[]
}

export interface NotizFilter {
  tag?: string
  personId?: number
  /** nur archivierte Notizen */
  archiv?: boolean
}

/** Eine Aufnahme, wie sie in der Offline-Warteschlange liegt und hochgeladen wird. */
export interface Aufnahme {
  clientId: string
  audio: Blob
  typ: string
  aufgenommenAm: string
  zeitzone: string
  dauerMs: number
  /** Die Nachfrage der App, die diese Aufnahme beantwortet */
  nachfrageId?: number
}

export type NachfrageRhythmus = 'AUS' | 'TAEGLICH' | 'ALLE_3_TAGE' | 'WOECHENTLICH'

export interface NachfrageEinstellung {
  rhythmus: NachfrageRhythmus
  uhrzeit: string
  zeitzone: string
  weckruf: boolean
}

export interface Nachfrage {
  id: number
  frage: string
  erstelltAm: string
  /** ERINNERN: Gedächtnisstütze zu Gespeichertem; VERKNUEPFEN: möglicher Zusammenhang zwischen Notizen */
  art: 'ERINNERN' | 'VERKNUEPFEN'
  /** Bei ERINNERN die Antwort aus den Notizen; wird nur auf Wunsch gezeigt */
  aufloesung: string | null
}

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    meldung: string,
  ) {
    super(meldung)
  }
}

function csrfToken(): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith('XSRF-TOKEN='))
    ?.substring('XSRF-TOKEN='.length)
}

async function anfrage<T>(methode: string, pfad: string, body?: unknown, roh?: URLSearchParams | FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = csrfToken()
  if (token && methode !== 'GET') headers['X-XSRF-TOKEN'] = decodeURIComponent(token)
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const antwort = await fetch(pfad, {
    method: methode,
    headers,
    credentials: 'same-origin',
    body: roh ?? (body !== undefined ? JSON.stringify(body) : undefined),
  })
  if (!antwort.ok) {
    let meldung = `${antwort.status} ${antwort.statusText}`
    if (antwort.status === 429) meldung = 'Zu viele Fehlversuche. Bitte später erneut versuchen.'
    try {
      const fehler = await antwort.json()
      if (fehler?.fehler) meldung = fehler.fehler
    } catch {
      // keine JSON-Fehlermeldung
    }
    throw new ApiFehler(antwort.status, meldung)
  }
  if (antwort.status === 204) return undefined as T
  return antwort.json() as Promise<T>
}

const get = <T,>(pfad: string) => anfrage<T>('GET', pfad)
const post = <T,>(pfad: string, body?: unknown) => anfrage<T>('POST', pfad, body)
const put = <T,>(pfad: string, body: unknown) => anfrage<T>('PUT', pfad, body)
const del = (pfad: string) => anfrage<void>('DELETE', pfad)

/** Ohne Anmeldung: vorher das CSRF-Cookie holen */
async function oeffentlich<T>(pfad: string, body: unknown): Promise<T> {
  await anfrage<unknown>('GET', '/api/health')
  return post<T>(pfad, body)
}

function aufnahmeFormular(a: Aufnahme): FormData {
  const form = new FormData()
  form.append('audio', new File([a.audio], 'aufnahme', { type: a.typ }))
  form.append('clientId', a.clientId)
  form.append('aufgenommenAm', a.aufgenommenAm)
  form.append('zeitzone', a.zeitzone)
  form.append('dauerMs', String(Math.round(a.dauerMs)))
  if (a.nachfrageId) form.append('nachfrageId', String(a.nachfrageId))
  return form
}

export const api = {
  health: () => get<{ status: string }>('/api/health'),
  ich: () => get<{ benutzername: string; admin: boolean }>('/api/auth/ich'),
  anmelden: async (benutzername: string, passwort: string) => {
    await api.health() // stellt sicher, dass das CSRF-Cookie gesetzt ist
    return anfrage<void>('POST', '/api/auth/login', undefined, new URLSearchParams({ username: benutzername, password: passwort }))
  },
  abmelden: () => post<void>('/api/auth/logout'),
  status: () => get<{ kiBereit: boolean; rueckfragen: number; terminvorschlaege: number; freundschaftsanfragen: number; neueGeteilte: number; faelligeErinnerungen: number }>('/api/status'),

  notizen: (vor?: string, filter: NotizFilter = {}) => {
    const p = new URLSearchParams()
    if (vor) p.set('vor', vor)
    if (filter.tag) p.set('tag', filter.tag)
    if (filter.personId) p.set('personId', String(filter.personId))
    if (filter.archiv) p.set('archiv', 'true')
    return get<{ notizen: Notiz[]; weitere: boolean }>(`/api/notizen${p.size ? `?${p}` : ''}`)
  },
  notiz: (id: number) => get<Notiz>(`/api/notizen/${id}`),
  aufnahmeHochladen: (a: Aufnahme) => anfrage<Notiz>('POST', '/api/notizen', undefined, aufnahmeFormular(a)),
  textNotiz: (e: { clientId: string; aufgenommenAm: string; zeitzone: string; text: string; nachfrageId?: number }) => post<Notiz>('/api/notizen/text', e),
  transkriptAendern: (id: number, transkript: string) => put<Notiz>(`/api/notizen/${id}/transkript`, { transkript }),
  wiederholen: (id: number) => post<Notiz>(`/api/notizen/${id}/wiederholen`),
  archivieren: (id: number, archiviert: boolean) => put<Notiz>(`/api/notizen/${id}/archiviert`, { archiviert }),
  notizLoeschen: (id: number) => del(`/api/notizen/${id}`),
  audioUrl: (id: number) => `/api/notizen/${id}/audio`,

  personen: (suche = '') => get<PersonUebersicht[]>(`/api/personen${suche.trim() ? `?${new URLSearchParams({ suche })}` : ''}`),
  person: (id: number) => get<{ person: PersonUebersicht; fakten: Fakt[] }>(`/api/personen/${id}`),
  personAliasLoesen: (id: number, alias: string) => post<{ notizen: number }>(`/api/personen/${id}/aliase/loesen`, { alias }),
  personAendern: (id: number, e: { name: string; beschreibung: string; aliase: string[] }) =>
    put<{ person: PersonUebersicht; fakten: Fakt[] }>(`/api/personen/${id}`, e),
  personenZusammenfuehren: (id: number, mitId: number) => post<{ person: PersonUebersicht; fakten: Fakt[] }>(`/api/personen/${id}/zusammenfuehren`, { mitId }),
  personLoeschen: (id: number) => del(`/api/personen/${id}`),
  faktLoeschen: (personId: number, faktId: number) => del(`/api/personen/${personId}/fakten/${faktId}`),

  korrekturPruefen: (notizId: number, alt: string, neu: string) => post<KorrekturAngebot[]>('/api/korrekturen/pruefen', { notizId, alt, neu }),
  korrekturAnwenden: (falsch: string, richtig: string, notizIds: number[]) =>
    post<{ geaendert: number }>('/api/korrekturen/anwenden', { falsch, richtig, notizIds }),
  korrekturVorschlaege: () => get<KorrekturVorschlag[]>('/api/korrekturen/vorschlaege'),
  korrekturVorschlagAnnehmen: (id: number, notizIds: number[]) => post<{ geaendert: number }>(`/api/korrekturen/vorschlaege/${id}/annehmen`, { notizIds }),
  korrekturVorschlagAblehnen: (id: number) => del(`/api/korrekturen/vorschlaege/${id}`),
  notizPruefen: (id: number) => post<{ rueckfragen: number }>(`/api/pruefung/notizen/${id}`),
  allePruefen: () => post<{ offen: number }>('/api/pruefung/alle'),
  pruefStand: () => get<{ offen: number }>('/api/pruefung/stand'),
  pruefFragen: () => get<PruefFrage[]>('/api/pruefung/fragen'),
  pruefSchreibweise: (id: number, richtig: string) => post<KorrekturAngebot>(`/api/pruefung/fragen/${id}/schreibweise`, { richtig }),
  pruefVerschieden: (id: number) => post<void>(`/api/pruefung/fragen/${id}/verschieden`),
  pruefKlarstellen: (id: number, antwort: string) => post<{ notizId: number }>(`/api/pruefung/fragen/${id}/klarstellen`, { antwort }),
  pruefIgnorieren: (id: number) => del(`/api/pruefung/fragen/${id}`),
  antwortVerwerfen: (id: number) => del(`/api/antworten/${id}`),
  schreibweisen: () => get<Schreibweise[]>('/api/korrekturen'),
  schreibweiseVergessen: (id: number) => del(`/api/korrekturen/${id}`),

  ueberMich: () => get<Fakt[]>('/api/ich'),
  ueberMichHinzufuegen: (fakt: string) => post<Fakt[]>('/api/ich/fakten', { fakt }),
  ueberMichLoeschen: (id: number) => del(`/api/ich/fakten/${id}`),

  klaerungen: () => get<KlaerungAnsicht[]>('/api/klaerungen'),
  klaerungZuordnen: (id: number, personId: number) => post<{ personId: number }>(`/api/klaerungen/${id}/zuordnen`, { personId }),
  klaerungNeu: (id: number) => post<{ personId: number }>(`/api/klaerungen/${id}/neu`),
  klaerungIgnorieren: (id: number) => del(`/api/klaerungen/${id}`),

  termine: () => get<{ anstehend: Termin[]; vergangen: Termin[] }>('/api/termine'),
  terminBestaetigen: (id: number) => post<Termin>(`/api/termine/${id}/bestaetigen`),
  terminVerwerfen: (id: number) => post<Termin>(`/api/termine/${id}/verwerfen`),
  terminAendern: (id: number, e: TerminEingabe) => put<Termin>(`/api/termine/${id}`, e),
  terminLoeschen: (id: number) => del(`/api/termine/${id}`),
  terminKalenderUrl: (id: number) => `/api/termine/${id}/kalender.ics`,
  erinnerungen: () => get<Erinnerung[]>('/api/erinnerungen'),
  nachfrageEinstellung: () => get<NachfrageEinstellung>('/api/nachfragen/einstellung'),
  nachfrageEinstellungSpeichern: (e: NachfrageEinstellung) => put<NachfrageEinstellung>('/api/nachfragen/einstellung', e),
  nachfrageAktuell: () => get<{ nachfrage: Nachfrage | null }>('/api/nachfragen/aktuell').then((r) => r.nachfrage),
  nachfrageJetzt: () => post<{ nachfrage: Nachfrage | null }>('/api/nachfragen/jetzt').then((r) => r.nachfrage),
  nachfrageUebersprungen: (id: number) => post<void>(`/api/nachfragen/${id}/uebersprungen`),
  nachfrageNie: (id: number) => post<void>(`/api/nachfragen/${id}/nie`),
  erinnerungenErledigt: () => get<Erinnerung[]>('/api/erinnerungen?erledigt=true'),
  erinnerungErledigt: (id: number) => post<void>(`/api/erinnerungen/${id}/erledigt`),
  erinnerungSpaeter: (id: number, minuten: number) => post<Erinnerung>(`/api/erinnerungen/${id}/spaeter`, { minuten }),
  erinnerungLoeschen: (id: number) => del(`/api/erinnerungen/${id}`),
  erinnerungKalenderUrl: (id: number) => `/api/erinnerungen/${id}/kalender.ics`,
  pushStand: () => get<PushStand>('/api/push'),
  pushAbonnieren: (endpoint: string, schluessel?: { p256dh?: string; auth?: string }) => post<PushStand>('/api/push/abo', { endpoint, ...schluessel }),
  pushAbbestellen: (endpoint: string) => post<PushStand>('/api/push/abo/entfernen', { endpoint }),
  pushTest: () => post<{ geraete: number; zugestellt: number }>('/api/push/test'),

  /** Gesprochene Frage in Text wandeln; die Aufnahme wird auf dem Server nicht gespeichert */
  frageTranskribieren: (audio: Blob, typ: string) => {
    const form = new FormData()
    form.append('audio', new File([audio], 'frage', { type: typ }))
    return anfrage<{ text: string }>('POST', '/api/fragen/sprache', undefined, form)
  },
  // eigenes Konto
  profil: () => get<Profil>('/api/konto'),
  passwortAendern: (aktuell: string, neu: string) => put<{ abgemeldet: number }>('/api/konto/passwort', { aktuell, neu }),
  emailAendern: (email: string) => put<void>('/api/konto/email', { email }),
  andereAbmelden: () => post<{ abgemeldet: number }>('/api/konto/andere-abmelden'),

  // Teilen: eigene Notiz freigeben
  freigaben: (notizId: number) => get<Freigabe[]>(`/api/notizen/${notizId}/freigaben`),
  freigabenSetzen: (notizId: number, empfaenger: number[]) => put<Freigabe[]>(`/api/notizen/${notizId}/freigaben`, { empfaenger }),
  // Teilen: mit mir geteilte Notizen
  geteilte: () => get<GeteilteNotiz[]>('/api/geteilt'),
  geteilteGesehen: () => post<void>('/api/geteilt/gesehen'),
  geteilt: (id: number) => get<GeteilteNotiz>(`/api/geteilt/${id}`),
  geteiltAudioUrl: (id: number) => `/api/geteilt/${id}/audio`,
  geteiltAusblenden: (id: number) => del(`/api/geteilt/${id}`),
  geteiltUebernehmen: (id: number) => post<Notiz>(`/api/geteilt/${id}/uebernehmen`),

  // Freunde (Anfragen nur per genau bekanntem Namen oder bestätigter Adresse)
  freunde: () => get<FreundeAnsicht>('/api/freunde'),
  freundAnfragen: (an: string) => post<void>('/api/freunde/anfragen', { an }),
  anfrageZurueckziehen: (id: number) => del(`/api/freunde/anfragen/${id}`),
  anfrageAnnehmen: (benutzerId: number) => post<void>(`/api/freunde/eingehend/${benutzerId}/annehmen`),
  anfrageAblehnen: (benutzerId: number) => post<void>(`/api/freunde/eingehend/${benutzerId}/ablehnen`),
  absenderSperren: (benutzerId: number) => post<void>(`/api/freunde/eingehend/${benutzerId}/sperren`),
  freundschaftBeenden: (benutzerId: number) => del(`/api/freunde/${benutzerId}`),
  freundSperren: (benutzerId: number) => post<void>(`/api/freunde/${benutzerId}/sperren`),
  entsperren: (benutzerId: number) => del(`/api/freunde/gesperrt/${benutzerId}`),

  // Mail-Links und "Passwort vergessen" (ohne Anmeldung); Tokens immer im Body, nie in der URL
  einladungPruefen: (token: string) => oeffentlich<{ benutzername: string }>('/api/auth/einladung/pruefen', { token }),
  einladungAnnehmen: (token: string, passwort: string) => oeffentlich<void>('/api/auth/einladung', { token, passwort }),
  passwortZuruecksetzen: (token: string, passwort: string) => oeffentlich<void>('/api/auth/passwort-zuruecksetzen', { token, passwort }),
  emailBestaetigen: (token: string) => oeffentlich<void>('/api/auth/email-bestaetigen', { token }),
  passwortVergessen: (benutzername: string) => oeffentlich<void>('/api/auth/passwort-vergessen', { benutzername }),

  // Verwaltung (nur Admin)
  benutzer: () => get<BenutzerEintrag[]>('/api/admin/benutzer'),
  benutzerEinladen: (benutzername: string, email: string, rolle: 'ADMIN' | 'NUTZER') =>
    post<BenutzerEintrag>('/api/admin/benutzer', { benutzername, email, rolle }),
  einladungSenden: (id: number) => post<void>(`/api/admin/benutzer/${id}/einladung`),
  resetSenden: (id: number) => post<void>(`/api/admin/benutzer/${id}/reset`),
  benutzerSperren: (id: number) => post<BenutzerEintrag>(`/api/admin/benutzer/${id}/sperren`),
  benutzerEntsperren: (id: number) => post<BenutzerEintrag>(`/api/admin/benutzer/${id}/entsperren`),
  benutzerLoeschen: (id: number) => del(`/api/admin/benutzer/${id}`),
  schluesselstand: () => get<Schluesselstand>('/api/admin/schluessel'),
  umschluesseln: () => post<Schluesselstand>('/api/admin/schluessel/umschluesseln'),

  fragen: (frage: string) =>
    post<Antwort>('/api/fragen', { frage, zeitzone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
}
